import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const InitialInputScreen = ({ user }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      toast.error('Please describe your decision');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/decisions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: prompt.trim() })
      });

      if (response.ok) {
        const decision = await response.json();
        navigate(`/decide/${decision.id}/edit`);
      } else {
        toast.error('Failed to process your decision');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-16 fade-in">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex flex-col items-center justify-center mb-4">
          <Sparkles className="w-10 h-10 text-purple-500 mb-2" />
          <h1 className="text-6xl font-bold gradient-text" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            DECIDE
          </h1>
        </div>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Stop overthinking.<br />Your wisdom knows the way.
        </p>
      </div>

      {/* User Nav */}
      {user && (
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/decisions')}
            className="btn-outline"
            data-testid="view-decisions-btn"
          >
            View My Decisions
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="glass-card p-10">
        <form onSubmit={handleSubmit}>
          <textarea
            data-testid="decision-input"
            className="textarea-field"
            style={{ borderRadius: '4px' }}
            placeholder="What are your options? Share your situation, how it feels, and any signs you've noticed. Your wisdom is not only in your mind - your body & the universe have wisdom too."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={8}
            disabled={loading}
          />

          <p className="text-gray-500 text-sm mt-3 italic text-center">
            Share whatever's on your mind - your thoughts, how it feels in your body, any signs you've noticed. There's no wrong way to describe it.
          </p>

          {/* Example Section - Collapsible */}
          <div className="mt-4 flex justify-center">
            {!showExample ? (
              <button
                type="button"
                onClick={() => setShowExample(true)}
                className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 transition-colors"
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-purple-600 hover:border-purple-700 font-semibold">
                  ?
                </span>
                <span>Not sure what to include? Click here for an example</span>
              </button>
            ) : (
              <div className="p-4 bg-purple-50 rounded-xl shadow-md">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-purple-900">
                    Here's an example:
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowExample(false)}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Hide
                  </button>
                </div>
                <div 
                  className="text-sm text-gray-700 leading-relaxed cursor-pointer hover:bg-purple-100 p-3 rounded transition-colors"
                  onClick={() => {
                    setPrompt(`Should I accept the job in San Francisco or stay in Austin? 

The SF job pays $200k with great benefits, but I'd be far from my family and friends. My gut says the timing isn't quite right, but the money is tempting.

I keep seeing signs about "staying grounded" everywhere, and my horoscope mentioned "home is where the heart is." 

When I think about SF, my body feels tense and contracted. When I think about staying in Austin, I feel relieved and at peace - even though I'm worried about missing out on the money.`);
                    setShowExample(false);
                  }}
                  title="Click to use this example"
                >
                  <p className="mb-2">
                    <strong className="text-purple-900">Should I accept the job in San Francisco or stay in Austin?</strong>
                  </p>
                  <p className="mb-2">
                    The SF job pays $200k with great benefits, but I'd be far from my family and friends. My gut says the timing isn't quite right, but the money is tempting.
                  </p>
                  <p className="mb-2">
                    I keep seeing signs about "staying grounded" everywhere, and my horoscope mentioned "home is where the heart is."
                  </p>
                  <p>
                    When I think about SF, my body feels tense and contracted. When I think about staying in Austin, I feel relieved and at peace - even though I'm worried about missing out on the money.
                  </p>
                  <p className="text-purple-600 text-xs mt-2 font-medium">
                    ✨ Click to use this example
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="submit"
              className="btn-primary flex items-center justify-center gap-2"
              disabled={loading || !prompt.trim()}
              data-testid="continue-btn"
            >
              {loading ? (
                <>
                  <div className="loading-spinner w-5 h-5"></div>
                  <span>Processing</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <p className="text-center text-gray-500 mt-8 text-sm">
        No login required to start. You'll be asked to create an account only after you make your decision.
      </p>
    </div>
  );
};

export default InitialInputScreen;
